import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api, type CodeChallenge } from "../lib/api";
import { ErrorText, Field } from "./ui";

// "Your account": change the sign-in email or password. Both need codes:
// a password change sends one to the sign-in email; an email change sends
// one to the current address, then one to the new address.

type Purpose = "password_change" | "email_change_old" | "email_change_new";

function CodeStep({ ch, purpose, label, busy, error, onSubmit, onCancel }: {
  ch: CodeChallenge; purpose: Purpose; label: string; busy: boolean; error: unknown; onSubmit: (code: string) => void; onCancel: () => void;
}) {
  const [code, setCode] = useState("");
  const [resent, setResent] = useState(false);
  const [resendError, setResendError] = useState<unknown>(null);
  return (
    <form className="form" onSubmit={(e) => { e.preventDefault(); onSubmit(code); }}>
      <p className="small">{label} <b>{ch.email_hint}</b>.</p>
      <input className="code-input" aria-label="Code" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" autoFocus
        value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
      <ErrorText error={error ?? resendError} />
      <div className="inline">
        <button type="submit" disabled={busy || code.length !== 6}>{busy ? "Checking…" : "Confirm"}</button>
        <button type="button" className="secondary" disabled={resent} onClick={async () => {
          setResendError(null);
          try { await api("/v1/auth/account-code/resend", { method: "POST", json: { challenge_token: ch.challenge_token, purpose } }); setResent(true); } catch (e) { setResendError(e); }
        }}>{resent ? "New code sent" : "Send a new code"}</button>
        <span className="spacer" />
        <button type="button" className="link" onClick={onCancel}>Cancel</button>
      </div>
    </form>
  );
}

export function SignInDetails({ email, hasPassword }: { email: string; hasPassword: boolean }) {
  const [mode, setMode] = useState<"" | "email" | "password">("");
  const [done, setDone] = useState("");
  return (
    <section className="card">
      <h2>Sign-in details</h2>
      <dl className="details">
        <dt>Email</dt><dd>{email}</dd>
        <dt>Password</dt><dd>{hasPassword ? "Set" : "Not set (you sign in with Google)"}</dd>
      </dl>
      {done && <p className="notice">{done}</p>}
      {mode === "" && (
        <div className="inline" style={{ marginTop: ".9rem" }}>
          <button className="secondary" onClick={() => { setDone(""); setMode("email"); }}>Change email</button>
          <button className="secondary" onClick={() => { setDone(""); setMode("password"); }}>{hasPassword ? "Change password" : "Set a password"}</button>
        </div>
      )}
      {mode === "email" && <ChangeEmail hasPassword={hasPassword} onClose={(msg) => { setMode(""); setDone(msg); }} />}
      {mode === "password" && <ChangePassword hasPassword={hasPassword} onClose={(msg) => { setMode(""); setDone(msg); }} />}
    </section>
  );
}

function ChangePassword({ hasPassword, onClose }: { hasPassword: boolean; onClose: (msg: string) => void }) {
  const qc = useQueryClient();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [ch, setCh] = useState<CodeChallenge | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const mismatch = again !== "" && again !== next;
  if (ch) {
    return (
      <div className="card inset">
        <CodeStep ch={ch} purpose="password_change" label="We emailed a code to" busy={busy} error={error} onCancel={() => onClose("")} onSubmit={async (code) => {
          setBusy(true); setError(null);
          try {
            await api("/v1/auth/password", { method: "POST", json: { current_password: current, new_password: next, challenge_token: ch.challenge_token, code } });
            await qc.invalidateQueries({ queryKey: ["account-security"] });
            onClose("Password changed. You were signed out on your other devices.");
          } catch (e) { setError(e); } finally { setBusy(false); }
        }} />
      </div>
    );
  }
  return (
    <form className="form card inset" onSubmit={async (e) => {
      e.preventDefault();
      if (mismatch) return;
      setBusy(true); setError(null);
      try { setCh(await api<CodeChallenge>("/v1/auth/password/start", { method: "POST", json: { current_password: current, new_password: next } })); } catch (err) { setError(err); } finally { setBusy(false); }
    }}>
      {hasPassword && <Field label="Current password"><input type="password" autoComplete="current-password" maxLength={256} required value={current} onChange={(e) => setCurrent(e.target.value)} /></Field>}
      <Field label="New password (at least 10 characters)"><input type="password" autoComplete="new-password" minLength={10} maxLength={256} required value={next} onChange={(e) => setNext(e.target.value)} /></Field>
      <Field label="New password again"><input type="password" autoComplete="new-password" maxLength={256} required value={again} onChange={(e) => setAgain(e.target.value)} /></Field>
      {mismatch && <p className="error">The two passwords don't match.</p>}
      <ErrorText error={error} />
      <div className="inline">
        <button type="submit" disabled={busy || mismatch}>{busy ? "Sending code…" : "Continue"}</button>
        <button type="button" className="link" onClick={() => onClose("")}>Cancel</button>
      </div>
    </form>
  );
}

function ChangeEmail({ hasPassword, onClose }: { hasPassword: boolean; onClose: (msg: string) => void }) {
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [step, setStep] = useState<{ ch: CodeChallenge; purpose: "email_change_old" | "email_change_new" } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  if (step) {
    const first = step.purpose === "email_change_old";
    return (
      <div className="card inset">
        <ol className="steps" aria-label="Steps">
          <li className={first ? "on" : "done"}>Your current email</li>
          <li className={first ? "" : "on"}>Your new email</li>
        </ol>
        <CodeStep key={step.purpose} ch={step.ch} purpose={step.purpose} busy={busy} error={error}
          label={first ? "First, to make sure it's you: enter the code we emailed to your current address," : "Now, to make sure the new address is right: enter the code we emailed to"}
          onCancel={() => onClose("")} onSubmit={async (code) => {
            setBusy(true); setError(null);
            try {
              if (first) {
                setStep({ ch: await api<CodeChallenge>("/v1/auth/email/confirm-old", { method: "POST", json: { challenge_token: step.ch.challenge_token, code } }), purpose: "email_change_new" });
              } else {
                const out = await api<{ email: string }>("/v1/auth/email/confirm-new", { method: "POST", json: { challenge_token: step.ch.challenge_token, code } });
                await qc.invalidateQueries({ queryKey: ["me"] });
                await qc.invalidateQueries({ queryKey: ["account-security"] });
                onClose(`You now sign in with ${out.email}. You were signed out on your other devices, and your old address was told.`);
              }
            } catch (e) { setError(e); } finally { setBusy(false); }
          }} />
      </div>
    );
  }
  return (
    <form className="form card inset" onSubmit={async (e) => {
      e.preventDefault();
      setBusy(true); setError(null);
      try { setStep({ ch: await api<CodeChallenge>("/v1/auth/email/start", { method: "POST", json: { password, new_email: newEmail } }), purpose: "email_change_old" }); } catch (err) { setError(err); } finally { setBusy(false); }
    }}>
      <Field label="New email"><input type="email" autoComplete="email" maxLength={254} required value={newEmail} onChange={(e) => setNewEmail(e.target.value)} /></Field>
      {hasPassword && <Field label="Your password"><input type="password" autoComplete="current-password" maxLength={256} required value={password} onChange={(e) => setPassword(e.target.value)} /></Field>}
      <p className="muted small">We'll email a code to your current address first, then one to the new address.</p>
      <ErrorText error={error} />
      <div className="inline">
        <button type="submit" disabled={busy}>{busy ? "Sending code…" : "Continue"}</button>
        <button type="button" className="link" onClick={() => onClose("")}>Cancel</button>
      </div>
    </form>
  );
}
